import React, { useCallback, useEffect, useMemo, useState } from 'react';

// Every app registered on a node in the operator's visible networks (GET /api/apps).
// A row opens its node in the topology.
export default function AppsPanel({ onOpenNode }) {
  const [apps, setApps] = useState(null);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState('');

  const refresh = useCallback(async () => {
    setError(null);
    try {
      const r = await fetch('/api/apps', { credentials: 'same-origin' });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      setApps(((await r.json()) || {}).entity || []);
    } catch (e) {
      setError(String(e.message || e));
      setApps([]);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const shown = useMemo(() => {
    const q = filter.trim().toLowerCase();
    if (!q || !apps) return apps;
    return apps.filter((a) => [a.name, a.provider, a.node_name, a.network?.display_name,
      ...(a.namespaces || [])].some((v) => (v || '').toLowerCase().includes(q)));
  }, [apps, filter]);

  return (
    <div style={{ padding: 20 }}>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 12, marginBottom: 12 }}>
        <h2 style={{ margin: 0, fontSize: '1.1rem' }}>Apps</h2>
        <button onClick={() => { setApps(null); refresh(); }} style={smallBtn}>refresh</button>
        <input value={filter} onChange={(e) => setFilter(e.target.value)} placeholder="filter"
               aria-label="filter apps"
               style={{ marginLeft: 'auto', fontSize: '0.8rem', padding: '3px 8px',
                        border: '1px solid #ccc', borderRadius: 3, width: 200 }} />
      </div>
      {apps === null && <div style={{ color: '#888' }}>Loading…</div>}
      {error && <div style={{ color: '#c00' }}>{error}</div>}
      {shown && shown.length === 0 && !error && (
        <div style={{ color: '#888', fontSize: '0.9rem' }}>
          {apps.length === 0 ? 'No apps registered.' : 'No apps match.'}
        </div>
      )}
      {shown && shown.length > 0 && (
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
          <thead>
            <tr style={{ borderBottom: '1px solid #ddd', textAlign: 'left' }}>
              <th style={th}>App</th>
              <th style={th}>Provider</th>
              <th style={th}>Namespaces</th>
              <th style={th}>Node</th>
              <th style={th}>Network</th>
            </tr>
          </thead>
          <tbody>
            {shown.map((a) => (
              <tr key={`${a.node_id}:${a.name}`} data-testid="app-row"
                  onClick={() => onOpenNode && onOpenNode(a)}
                  title="Open this node"
                  style={{ borderBottom: '1px solid #eee', cursor: 'pointer' }}>
                <td style={td}>
                  <strong>{a.name}</strong>
                  {!a.active && <span style={{ color: '#c44', marginLeft: 6 }}>(inactive)</span>}
                  {a.description && (
                    <div style={{ color: '#888', fontSize: '0.75rem', fontStyle: 'italic' }}>{a.description}</div>
                  )}
                </td>
                <td style={td}>{a.provider || '—'}</td>
                <td style={{ ...td, fontFamily: 'monospace', color: '#555' }}>
                  {(a.namespaces || []).join(', ') || '—'}
                </td>
                <td style={td}>{a.node_name || <code>{a.node_id}</code>}</td>
                <td style={td}>{a.network?.display_name || a.network?.canonical_name || '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

const th = { padding: '6px 8px' };
const td = { padding: '6px 8px', verticalAlign: 'top' };
const smallBtn = {
  background: 'none', border: '1px solid #ccc', borderRadius: '3px',
  padding: '2px 8px', fontSize: '0.75rem', cursor: 'pointer',
};
